import { useEffect } from 'react';
import { useSocket } from '@/hooks/useSocket';
import { usePostsStore } from '@/stores';
import {
  SOCIAL_EVENTS,
  type PostPublishedPayload,
  type PostFailedPayload,
  type PostSubmittedPayload,
  type PostEngagementPayload,
} from '@/services/socket/socketEvents';

/**
 * Subscribes to real-time post status and engagement socket events.
 * Updates usePostsStore in real-time. Should be called once in AppLayout.
 */
export function useSocialSocketEvents() {
  const { on, off, isConnected, connectionId } = useSocket();

  useEffect(() => {
    if (!isConnected) return;

    const handleSubmitted = (data: PostSubmittedPayload) => {
      usePostsStore.setState(
        (state) => ({
          posts: state.posts.map((p) =>
            p._id === data.postId ? { ...p, status: 'submitted' } : p
          ),
          scheduledPosts: state.scheduledPosts.map((p) =>
            p._id === data.postId ? { ...p, status: 'submitted' } : p
          ),
        }),
        false,
        'socket/post:submitted'
      );
    };

    const handlePublished = (data: PostPublishedPayload) => {
      usePostsStore.setState(
        (state) => ({
          posts: state.posts.map((p) =>
            p._id === data.postId ? { ...p, status: 'published' } : p
          ),
          scheduledPosts: state.scheduledPosts.map((p) =>
            p._id === data.postId ? { ...p, status: 'published' } : p
          ),
        }),
        false,
        'socket/post:published'
      );
    };

    const handleFailed = (data: PostFailedPayload) => {
      usePostsStore.setState(
        (state) => ({
          posts: state.posts.map((p) =>
            p._id === data.postId ? { ...p, status: 'failed' } : p
          ),
          scheduledPosts: state.scheduledPosts.map((p) =>
            p._id === data.postId ? { ...p, status: 'failed' } : p
          ),
        }),
        false,
        'socket/post:failed'
      );
    };

    const handleEngagement = (data: PostEngagementPayload) => {
      usePostsStore.setState(
        (state) => ({
          posts: state.posts.map((p) =>
            p._id === data.postId ? { ...p, engagement: data.engagement } : p
          ),
          scheduledPosts: state.scheduledPosts.map((p) =>
            p._id === data.postId ? { ...p, engagement: data.engagement } : p
          ),
        }),
        false,
        'socket/post:engagement'
      );
    };

    on(SOCIAL_EVENTS.POST_SUBMITTED, handleSubmitted);
    on(SOCIAL_EVENTS.POST_PUBLISHED, handlePublished);
    on(SOCIAL_EVENTS.POST_FAILED, handleFailed);
    on(SOCIAL_EVENTS.POST_ENGAGEMENT, handleEngagement);

    return () => {
      off(SOCIAL_EVENTS.POST_SUBMITTED, handleSubmitted);
      off(SOCIAL_EVENTS.POST_PUBLISHED, handlePublished);
      off(SOCIAL_EVENTS.POST_FAILED, handleFailed);
      off(SOCIAL_EVENTS.POST_ENGAGEMENT, handleEngagement);
    };
  }, [isConnected, connectionId, on, off]);
}
